import React, { useContext, useEffect, useRef } from 'react';
import { TinyColor } from '@ctrl/tinycolor';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import _ from 'lodash';
import { type RestSimpleChipLaCard } from '@bbkAdminUtils/api-client/rest-la-cards-types';
import { type RestModalForwards } from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import { type ElementType } from '@bbkAdminUtils/utility-types';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import ActionButtonIcons from '@bbkFunnel/components/modals/sections/action-button-helpers';
import { OtherActions } from '@bbkFunnel/components/modals/sections/other-actions';
import { FunnelButton } from '@bbkFunnel/components/v2';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import { RefsContext } from '@bbkFunnel/utils/refs.context';
import { FunnelContext } from '@bbkFunnelComponents/FunnelContext';
import getIconForItem from '@bbkFunnelComponents/loss/loss-icons';
import { LossGridItem } from '@bbkFunnelComponents/loss/sections/grid-item';
import { LossTitle } from '@bbkFunnelComponents/loss/sections/title';
import { makeClassNameSafe } from '@bbkFunnelUtils/utils';

import './loss-simple-chip.scss';

type ActivityItem = ElementType<RestSimpleChipLaCard['requires']['activities']>;

type Props = {
  card: RestSimpleChipLaCard & {
    style_overlay?: {
      fillIconColor?: string;
      light_background?: string;
      chip_value?: string;
    };
  };
  onClick?: () => void;
  handleLossCardAction?: ({ action }: { action: RestModalForwards }) => void;
  isHorizontal: boolean;
};

const Icon = styled.img`
  width: 100%;
  maxwidth: 22px;
`;

const IconContainer = styled.div<{ iconBg: string }>`
  background: ${({ iconBg }) => iconBg};
`;

const ListContainer = styled.div<{ isHorizontal: boolean }>`
  flex: 0 0 ${({ isHorizontal }) => (isHorizontal ? '25%' : '50%')};
`;

const ListItem = styled.div``;

const ListItemValue = styled.div<{ chipValue?: string }>`
  ${({ chipValue }) => chipValue ?? ''}
`;

const ListItemDesc = styled.div<{ color: string }>`
  color: ${({ color }) => color};
`;

const LossSimpleChip: React.FC<Props> = ({
  card,
  card: {
    id,
    max_redemptions_reached,
    requires: { activities, title, forwards, other_actions },
    style_overlay: { chip_value } = {},
  },
  onClick,
  handleLossCardAction,
  isHorizontal,
}) => {
  // console.log('[LossSimpleChip]', this.props);
  const theme = useTheme() as EmotionTheme;
  const { isPreview } = useContext(FunnelContext);
  const refsPrefix = id;
  const refsCtx = useContext(RefsContext);
  const titleRef = useRef<HTMLSpanElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}`, titleRef), [titleRef]);
  const imageRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-image`, imageRef), [imageRef]);
  const btnRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-btn`, btnRef), [btnRef]);

  const specificStyles = {
    backgroundColor: theme.buttonBackgroundColor,
    width: '100%',
  };

  const iconFill = _.get(
    card,
    'style_overlay.fillIconColor',
    theme.primaryColor
  );
  const iconFillClr = new TinyColor(iconFill);
  const iconBg = iconFillClr.isValid
    ? iconFillClr.setAlpha(0.1).toRgbString()
    : (_.get(card, 'style_overlay.light_background') as string);
  const descColor = new TinyColor(theme.textColor).lighten(20);

  const renderIcon = ({ image, icon }: ActivityItem) => {
    if (image) {
      return <Icon srcSet={`${image} 2x`} alt="" />;
    }

    if (icon) {
      return getIconForItem(icon, iconFill);
    }

    return undefined;
  };

  const listItems = activities.map((activity) => {
    const { icon, text, volume, unit } = activity;
    return (
      <ListContainer
        isHorizontal={isHorizontal}
        className="list-item"
        key={`${icon}_${text}_${volume}`}
      >
        <IconContainer iconBg={iconBg} className="li-icon">
          {renderIcon(activity)}
        </IconContainer>
        <ListItem className="li-info">
          <ListItemValue className="lii-value" chipValue={chip_value}>
            <CustomerHtml value={`${volume ?? '--'}${unit ?? ''}`} />
          </ListItemValue>
          <ListItemDesc className="lii-desc" color={descColor.toHexString()}>
            <CustomerHtml value={text} />
          </ListItemDesc>
        </ListItem>
      </ListContainer>
    );
  });

  return (
    <LossGridItem
      classes={cs('bbk-loss-simple-chip', makeClassNameSafe(card.name))}
      onClick={isPreview ? onClick : () => undefined}
    >
      <div
        className="d-flex flex-column justify-content-start"
        style={{ height: '100%' }}
      >
        <div>
          <LossTitle>
            <span ref={titleRef}>
              <CustomerHtml value={title} />
            </span>
          </LossTitle>
          <div className="bbk-loss-simple-chip-inner">
            <div className="list">{listItems}</div>
          </div>
        </div>
        <div>
          {forwards && (
            <div
              className={cs(
                'btn',
                'd-flex align-items-center justify-content-center',
                'mt-3'
              )}
              ref={btnRef}
            >
              <FunnelButton
                disabled={max_redemptions_reached}
                onClick={() => {
                  handleLossCardAction?.({ action: forwards });
                }}
                specificStyles={specificStyles}
              >
                {forwards?.icon && (
                  <ActionButtonIcons
                    styles={{ marginRight: 4 }}
                    icon={forwards.icon}
                  />
                )}
                {forwards.text}
              </FunnelButton>
            </div>
          )}
          {other_actions && (
            <OtherActions
              version2Template
              id={id}
              otherActions={other_actions}
              onClick={(action) => handleLossCardAction?.({ action })}
            />
          )}
        </div>
      </div>
    </LossGridItem>
  );
};
export default LossSimpleChip;
